import type { CampaignApplication } from '@/services/campaigns/types'
import type {
  ApiResponse,
  Asset,
  Campaign,
  CampaignState,
  CampaignStatusDetail,
  ContentFormatDetail,
  CurrencyDetail,
  IndustryDetail,
  LanguageDetail,
  SearchParams,
} from '@/types'
import { useLocation } from 'react-router-dom'
import api from '../services/api'
import { logError } from '../utils/sentry'
import { createPersistedStore } from './baseStore'

const initialState: CampaignState = {
  campaigns: [],
  languages: [],
  industries: [],
  currencies: [],
  contentFormats: [],
  campaignStatuses: [],
  campaignApplications: [],
  error: null,
  loading: false,
  isInitialized: false,
  pagination: {
    page: 1,
    pageSize: 12,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    items: [],
  },
  searchParams: {
    name: '',
    status: [],
  },
}

interface CampaignActions {
  initialize: () => Promise<void>
  getCampaigns: (params?: SearchParams) => Promise<
    | {
        items: Campaign[]
        page: number
        pageSize: number
        totalItems: number
        hasNextPage: boolean
        hasPreviousPage: boolean
      }
    | undefined
  >
  setPage: (page: number) => void
  setSearchParams: (params: SearchParams) => void
  createCampaign: (campaignData: Partial<Campaign>) => Promise<Campaign>
  updateCampaign: (id: string | number, campaignData: Partial<Campaign>) => Promise<Campaign | null>
  fetchCampaignDetails: (id: string | number) => Promise<Campaign | null>
  uploadAsset: (campaignId: string | number, file: File) => Promise<Asset | null>
  deleteAssets: (assetIds: string[]) => Promise<boolean>
  fetchCampaignAssets: (campaignId: string | number) => Promise<Asset[]>
  setCampaignApplications: (applications: CampaignApplication[]) => void
  updateApplicationStatus: (applicationId: number, status: string) => void
}

type SetState<T> = (state: T | Partial<T> | ((state: T) => T | Partial<T>), replace?: boolean) => void

type StoreState = CampaignState & CampaignActions

const actions = (set: SetState<CampaignState>, get: () => StoreState): CampaignActions => ({
  initialize: async (): Promise<void> => {
    const state = get()
    if (state.isInitialized) return

    try {
      const [languagesRes, industriesRes, currenciesRes, campaignStatusesRes] = await Promise.all([
        api.get('/languages'),
        api.get('/industries'),
        api.get('/currencies'),
        api.get('/campaign_statuses'),
      ])

      let languages: LanguageDetail[] = []
      let industries: IndustryDetail[] = []
      let currencies: CurrencyDetail[] = []
      const contentFormats: ContentFormatDetail[] = []
      let campaignStatuses: CampaignStatusDetail[] = []

      if (Array.isArray(languagesRes.data)) {
        languages = languagesRes.data
      } else if (typeof languagesRes.data === 'object' && languagesRes.data !== null) {
        if ('items' in languagesRes.data && Array.isArray(languagesRes.data.items)) {
          languages = languagesRes.data.items
        } else if (
          'data' in languagesRes.data &&
          typeof languagesRes.data.data === 'object' &&
          languagesRes.data.data !== null &&
          'items' in languagesRes.data.data &&
          Array.isArray(languagesRes.data.data.items)
        ) {
          languages = languagesRes.data.data.items
        }
      }

      if (Array.isArray(industriesRes.data)) {
        industries = industriesRes.data
      } else if (typeof industriesRes.data === 'object' && industriesRes.data !== null) {
        if ('items' in industriesRes.data && Array.isArray(industriesRes.data.items)) {
          industries = industriesRes.data.items
        } else if (
          'data' in industriesRes.data &&
          typeof industriesRes.data.data === 'object' &&
          industriesRes.data.data !== null &&
          'items' in industriesRes.data.data &&
          Array.isArray(industriesRes.data.data.items)
        ) {
          industries = industriesRes.data.data.items
        }
      }

      if (Array.isArray(currenciesRes.data)) {
        currencies = currenciesRes.data
      } else if (typeof currenciesRes.data === 'object' && currenciesRes.data !== null) {
        if ('items' in currenciesRes.data && Array.isArray(currenciesRes.data.items)) {
          currencies = currenciesRes.data.items
        } else if (
          'data' in currenciesRes.data &&
          typeof currenciesRes.data.data === 'object' &&
          currenciesRes.data.data !== null &&
          'items' in currenciesRes.data.data &&
          Array.isArray(currenciesRes.data.data.items)
        ) {
          currencies = currenciesRes.data.data.items
        }
      }

      if (Array.isArray(campaignStatusesRes.data)) {
        campaignStatuses = campaignStatusesRes.data
      } else if (typeof campaignStatusesRes.data === 'object' && campaignStatusesRes.data !== null) {
        if ('items' in campaignStatusesRes.data && Array.isArray(campaignStatusesRes.data.items)) {
          campaignStatuses = campaignStatusesRes.data.items
        } else if (
          'data' in campaignStatusesRes.data &&
          typeof campaignStatusesRes.data.data === 'object' &&
          campaignStatusesRes.data.data !== null &&
          'items' in campaignStatusesRes.data.data &&
          Array.isArray(campaignStatusesRes.data.data.items)
        ) {
          campaignStatuses = campaignStatusesRes.data.data.items
        }
      }

      set({
        languages,
        industries,
        currencies,
        contentFormats,
        campaignStatuses,
        isInitialized: true,
      })
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize'
      logError('Failed to initialize campaign store:', { error })
      set({ error: errorMessage })
    }
  },

  getCampaigns: async (
    params: SearchParams = {},
  ): Promise<
    | {
        items: Campaign[]
        page: number
        pageSize: number
        totalItems: number
        hasNextPage: boolean
        hasPreviousPage: boolean
      }
    | undefined
  > => {
    const location = useLocation().pathname
    if (location !== '/campaigns') return

    try {
      set({ loading: true, error: null })
      const state = get()

      const queryParams = new URLSearchParams()
      if (params.name) queryParams.append('name', params.name)
      if (params.status?.length) {
        for (const status of params.status) {
          queryParams.append('status', status)
        }
      }

      if (state.pagination.page !== undefined) {
        queryParams.append('p', String(state.pagination.page))
      }

      if (state.pagination.pageSize !== undefined) {
        queryParams.append('ps', String(state.pagination.pageSize))
      }

      try {
        const response = await api.get<{
          items: Campaign[]
          page: number
          pageSize: number
          totalItems: number
          hasNextPage: boolean
          hasPreviousPage: boolean
        }>(`/brands/campaigns?${queryParams.toString()}`)

        const responseData = response?.data || {
          items: [],
          page: state.pagination.page,
          pageSize: state.pagination.pageSize,
          totalItems: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        }

        const { items = [], page = 1, pageSize = 10, totalItems = 0, hasNextPage = false, hasPreviousPage = false } = responseData

        set({
          campaigns: Array.isArray(items) ? items.filter((item) => item !== null) : [],
          pagination: {
            page,
            pageSize,
            totalItems,
            hasNextPage,
            hasPreviousPage,
            items: [],
          },
          searchParams: {
            ...state.searchParams,
            ...params,
          },
          loading: false,
        })
        return responseData
      } catch (apiError) {
        console.error('API request error:', apiError)

        const errorMessage = apiError instanceof Error ? apiError.message : 'API request failed'
        set({ loading: false, error: errorMessage })
        return undefined
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch campaigns'
      logError('Failed to fetch campaigns:', { error })
      set({
        loading: false,
        error: errorMessage,
        campaigns: [],
      })
      return {
        items: [],
        page: 1,
        pageSize: 10,
        totalItems: 0,
        hasNextPage: false,
        hasPreviousPage: false,
      }
    }
  },

  setPage: (page: number): void => {
    const state = get()
    set({
      pagination: {
        ...state.pagination,
        page,
      },
    })

    const getState = get()
    getState.getCampaigns({
      page,
      pageSize: state.pagination.pageSize,
      ...state.searchParams,
    })
  },

  setSearchParams: (params: SearchParams): void => {
    const state = get()
    set({
      searchParams: {
        ...state.searchParams,
        ...params,
      },
    })
  },

  createCampaign: async (campaignData: Partial<Campaign>): Promise<Campaign> => {
    try {
      set({ loading: true, error: null })
      const response = await api.post<ApiResponse<Campaign> | Campaign>('/brands/campaigns', campaignData)

      let campaign: Campaign

      if (response.data) {
        if ('data' in response.data && response.data.data) {
          campaign = response.data.data
        } else if ('id' in response.data) {
          campaign = response.data as Campaign
        } else {
          console.error('API response has unexpected structure:', response.data)

          const errorMessage = 'API response has unexpected structure'
          set({ loading: false, error: errorMessage })
          campaign = {} as Campaign
        }
      } else {
        const error = new Error('API response missing data')
        logError('API response missing data:', { error })
        set({ loading: false, error: error.message })
        return {} as Campaign
      }

      const state = get()

      set({
        campaigns: [...state.campaigns, campaign].filter((item) => item !== null),
        loading: false,
      })
      return campaign
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create campaign'
      logError('Failed to create campaign:', { error })
      set({ loading: false, error: errorMessage })
      throw error
    }
  },

  updateCampaign: async (id: string | number, campaignData: Partial<Campaign>): Promise<Campaign | null> => {
    try {
      set({ loading: true, error: null })
      const response = await api.put<ApiResponse<Campaign> | Campaign>(`/brands/campaigns/${id}`, campaignData)

      let campaign: Campaign

      if (response.data) {
        if ('data' in response.data && response.data.data) {
          campaign = response.data.data
        } else if ('id' in response.data) {
          campaign = response.data as Campaign
        } else {
          console.error('API response has unexpected structure:', response.data)

          const errorMessage = 'API response has unexpected structure'
          set({ loading: false, error: errorMessage })
          return null
        }
      } else {
        logError('API response missing data')
        set({ loading: false, error: 'API response missing data' })
        return {} as Campaign
      }

      set({ loading: false })
      return campaign
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update campaign'
      logError('Failed to update campaign:', { error })
      set({ loading: false, error: errorMessage })
      return null
    }
  },

  fetchCampaignDetails: async (id: string | number): Promise<Campaign | null> => {
    try {
      const response = await api.get(`/brands/campaigns/${id}`)
      if (response?.data) {
        return response.data as Campaign
      }
      return null
    } catch (error) {
      logError('Failed to fetch campaign details:', { error })
      return null
    }
  },

  uploadAsset: async (campaignId: string | number, file: File): Promise<Asset | null> => {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post(`/brands/campaigns/${campaignId}/assets`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response?.data) {
        return response.data as Asset
      }
      return null
    } catch (error) {
      logError('Failed to upload asset:', { error })
      return null
    }
  },

  deleteAssets: async (assetIds: string[]): Promise<boolean> => {
    try {
      const validAssetIds = assetIds.filter((id) => id !== null && id !== undefined)

      if (validAssetIds.length === 0) {
        return true
      }

      await api.delete('/brands/assets', {
        data: {
          ids: validAssetIds,
        },
      })
      return true
    } catch (error) {
      logError('Failed to delete assets:', { error })
      return false
    }
  },

  fetchCampaignAssets: async (campaignId: string | number): Promise<Asset[]> => {
    try {
      const campaign = await actions(set, get).fetchCampaignDetails(campaignId)
      return campaign?.assets || []
    } catch (error) {
      logError('Failed to fetch campaign assets:', { error })
      return []
    }
  },

  setCampaignApplications: (applications: CampaignApplication[]): void => {
    set({ campaignApplications: applications })
  },

  updateApplicationStatus: (applicationId: number, status: string): void => {
    const state = get()
    const updatedApplications = state.campaignApplications.map((app) => (app.id === applicationId ? { ...app, status } : app))
    set({ campaignApplications: updatedApplications })
  },
})

const useCampaignStore = createPersistedStore<CampaignState, CampaignActions>('campaign-store', initialState, actions)

export default useCampaignStore
